/**
 * SchemasModal Component
 *
 * Displays all tenant schemas in a table with status, size, and table count.
 * Supports refresh functionality.
 *
 * @module features/tenants/components/modals/SchemasModal
 */

import { DatabaseIcon, RefreshIcon } from '../../../../components/common/Icons';
import type { TenantSchema } from '../../types';

interface SchemasModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** List of tenant schemas */
  schemas: TenantSchema[];
  /** Whether schemas are currently loading */
  loading: boolean;
  /** Callback to refresh schemas */
  onRefresh: () => void;
}

/**
 * Renders a modal displaying all tenant schemas.
 */
export const SchemasModal: React.FC<SchemasModalProps> = ({
  isOpen,
  onClose,
  schemas,
  loading,
  onRefresh,
}) => {
  if (!isOpen) {
    return null;
  }

  const activeCount = schemas.filter(s => !s.is_archived).length;
  const archivedCount = schemas.filter(s => s.is_archived).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DatabaseIcon size={24} />
            Tenant Schemas
          </h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {schemas.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
              <DatabaseIcon size={48} style={{ opacity: 0.5 }} />
              <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>
                No tenant schemas found
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Schemas will appear here when you provision schema-level tenants
              </p>
            </div>
          ) : (
            <>
              <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                background: 'rgba(124, 58, 237, 0.1)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
              }}>
                Total: <strong>{schemas.length}</strong> schemas
                ({activeCount} active,
                {' '}{archivedCount} archived)
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Schema Name</th>
                    <th>Tables</th>
                    <th>Size</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schemas.map((schema) => (
                    <tr key={schema.schema_name}>
                      <td>
                        <code style={{
                          background: 'rgba(0,0,0,0.2)',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                        }}>
                          {schema.schema_name}
                        </code>
                      </td>
                      <td className="text-secondary">{schema.table_count}</td>
                      <td className="text-secondary">{schema.size}</td>
                      <td>
                        {schema.is_archived ? (
                          <span className="badge badge-warning">Archived</span>
                        ) : (
                          <span className="badge badge-success">Active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button
            className="btn btn-ghost"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshIcon size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
